import { fbt } from 'fbtee'
import type { DateRange } from '@shared/ranking'
import type { ListSort } from './useListControls'
import { formatDayShort } from '../../lib/formatDate'

export function rangeLabel(range: DateRange): string {
  switch (range) {
    case 'today': return fbt('Today', 'Browse list date range: events happening today')
    case 'weekend': return fbt('This weekend', 'Browse list date range: Friday to Sunday of the current or coming weekend')
    case 'week': return fbt('Next 7 days', 'Browse list date range: the next seven days')
    case 'month': return fbt('Next 30 days', 'Browse list date range: the next thirty days')
    case 'all': return fbt('All upcoming', 'Browse list date range: every upcoming event')
  }
}

/** Shorter range label for the chip itself, so the Discover selector row fits one line; the menu keeps `rangeLabel`. */
export function rangeChipLabel(range: DateRange): string {
  switch (range) {
    case 'today': return rangeLabel(range)
    case 'weekend': return fbt('Weekend', 'Compact browse date-range chip: Friday to Sunday of the current or coming weekend')
    case 'week': return fbt('7 days', 'Compact browse date-range chip: the next seven days')
    case 'month': return fbt('30 days', 'Compact browse date-range chip: the next thirty days')
    case 'all': return fbt('Upcoming', 'Compact browse date-range chip: every upcoming event')
  }
}

export function sortLabel(sort: ListSort): string {
  switch (sort) {
    case 'relevance': return fbt('Relevance', 'Browse list sort order: most relevant events first')
    case 'date': return fbt('Date', 'Browse list sort order: soonest events first')
  }
}

/** Empty-state line when the chosen range has no events. */
export function emptyRangeText(range: DateRange): string {
  switch (range) {
    case 'today': return fbt('Nothing today.', 'Empty browse list for the Today date range')
    case 'weekend': return fbt('Nothing this weekend.', 'Empty browse list for the This weekend date range')
    case 'week': return fbt('Nothing in the next 7 days.', 'Empty browse list for the Next 7 days date range')
    case 'month': return fbt('Nothing in the next 30 days.', 'Empty browse list for the Next 30 days date range')
    case 'all': return fbt('Nothing coming up.', 'Empty browse list for the All upcoming date range')
  }
}

/** Button that widens an empty browse list to `range`. */
export function widenRangeLabel(range: DateRange): string {
  switch (range) {
    case 'today': return fbt('Show today', 'Button widening an empty browse list to Today')
    case 'weekend': return fbt('Show this weekend', 'Button widening an empty browse list to This weekend')
    case 'week': return fbt('Show the next 7 days', 'Button widening an empty browse list to the next 7 days')
    case 'month': return fbt('Show the next 30 days', 'Button widening an empty browse list to the next 30 days')
    case 'all': return fbt('Show all upcoming', 'Button widening an empty browse list to every upcoming event')
  }
}

/** Empty-state line when the single day picked from Home's calendar has no events. */
export function emptyDayText(day: string, locale: string): string {
  return fbt('Nothing on ' + fbt.param('date', formatDayShort(day, locale)) + '.', 'Empty browse list for a single day, e.g. "Nothing on Sat, Oct 18."')
}
